import axios from 'axios'
import api from './api'
import type {
  TourCreateRequest,
  BatchRoomUploadUrlRequest,
  BatchUploadUrlResponse,
  ConfirmUploadRequest,
  TourResponse,
  TourRoomResponse,
  TourStatus,
  RoomStatus,
  TourListItem,
} from '@/types/tour'

export interface TourStatusRoom {
  id: number
  room_order: number
  status: RoomStatus
  room_type: string | null
  video_s3_key: string | null
  current_step: string | null
  error_message: string | null
}

export interface TourStatusResponse {
  id: number
  status: TourStatus
  rooms: TourStatusRoom[]
}

export async function createTour(data: TourCreateRequest): Promise<TourResponse> {
  const res = await api.post<TourResponse>('/tours/', data)
  return res.data
}

export async function batchGetUploadUrls(
  tourId: number,
  rooms: BatchRoomUploadUrlRequest['rooms'],
): Promise<BatchUploadUrlResponse> {
  const res = await api.post<BatchUploadUrlResponse>(
    `/tours/${tourId}/rooms/batch-upload-urls`,
    { rooms },
  )
  return res.data
}

export async function uploadFileToS3(
  presignedUrl: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  await axios.put(presignedUrl, file, {
    headers: { 'Content-Type': file.type },
    onUploadProgress: onProgress
      ? (e) => {
          if (e.total) onProgress(Math.round((e.loaded / e.total) * 100))
        }
      : undefined,
  })
}

export async function confirmUpload(
  tourId: number,
  roomId: number,
  s3Key: string,
): Promise<void> {
  await api.post(`/tours/${tourId}/rooms/${roomId}/confirm-upload`, {
    s3_key: s3Key,
  } satisfies ConfirmUploadRequest)
}

/**
 * Analyse the staged rooms that need it; `roomIds` narrows the set further.
 * `useAiMoves` drops their chosen camera moves first so the analysis picks.
 */
export async function processTour(
  tourId: number,
  roomIds?: number[],
  { useAiMoves = false }: { useAiMoves?: boolean } = {},
): Promise<void> {
  const body = {
    ...(roomIds && roomIds.length > 0 ? { room_ids: roomIds } : {}),
    ...(useAiMoves ? { use_ai_moves: true } : {}),
  }
  await api.post(`/tours/${tourId}/process`, Object.keys(body).length ? body : undefined)
}

/** `regenerate`: the listed rooms may already have a clip; a new one replaces it. */
export async function generateTour(
  tourId: number,
  roomIds?: number[],
  { regenerate = false } = {},
): Promise<void> {
  const body = roomIds && roomIds.length > 0
    ? { room_ids: roomIds, ...(regenerate ? { regenerate: true } : {}) }
    : undefined
  await api.post(`/tours/${tourId}/generate`, body)
}

export async function getTour(tourId: number, signal?: AbortSignal): Promise<TourResponse> {
  const res = await api.get<TourResponse>(`/tours/${tourId}`, { signal })
  return res.data
}

export async function getTourStatus(tourId: number): Promise<TourStatusResponse> {
  const res = await api.get<TourStatusResponse>(`/tours/${tourId}/status`)
  return res.data
}

export interface PresignedUrlResponse {
  url: string
  expires_at: string
  s3_key: string
}

export type TourSort = 'updated_at' | 'created_at' | 'title'

export async function listTours(): Promise<TourListItem[]> {
  const res = await api.get<TourListItem[]>('/tours/')
  return res.data
}

/** Move a tour between folders. `null` puts it back in the ungrouped row. */
export async function updateTour(
  tourId: number,
  data: { title?: string; folder_id?: number | null },
): Promise<TourResponse> {
  const res = await api.patch<TourResponse>(`/tours/${tourId}`, data)
  return res.data
}

export async function deleteTour(tourId: number): Promise<void> {
  await api.delete(`/tours/${tourId}`)
}

export async function deleteRoom(tourId: number, roomId: number): Promise<void> {
  await api.delete(`/tours/${tourId}/rooms/${roomId}`)
}

/** Move rooms between the video's working set and its photo storage. */
export async function stageRooms(
  tourId: number,
  roomIds: number[],
  isStaged: boolean,
): Promise<{ updated: number[] }> {
  const res = await api.patch<{ updated: number[] }>(`/tours/${tourId}/rooms/stage`, {
    room_ids: roomIds,
    is_staged: isStaged,
  })
  return res.data
}

/** Save the row order (= clip order) as room_order. Unlisted rooms follow. */
export async function reorderRooms(tourId: number, roomIds: number[]): Promise<void> {
  await api.patch(`/tours/${tourId}/rooms/order`, { room_ids: roomIds })
}

/** Copy a room's photo (and analysis, if any) onto a new room. Free. */
/** How a room's current clip plays in the video. Free; never a new clip. */
export async function setRoomPlayback(
  tourId: number,
  roomId: number,
  playback: { speed?: number; reversed?: boolean },
): Promise<{ playback_speed: number; playback_reversed: boolean }> {
  const res = await api.patch(`/tours/${tourId}/rooms/${roomId}/playback`, playback)
  return res.data
}

/** A new room on the same photo. `withClip` shares the source's clip too. */
export async function duplicateRoom(
  tourId: number,
  roomId: number,
  withClip = false,
): Promise<TourRoomResponse> {
  const res = await api.post<TourRoomResponse>(
    `/tours/${tourId}/rooms/${roomId}/duplicate`,
    withClip ? { with_clip: true } : undefined,
  )
  return res.data
}

export async function getRoomVideoUrl(
  tourId: number,
  roomId: number,
): Promise<PresignedUrlResponse> {
  const res = await api.get<PresignedUrlResponse>(`/tours/${tourId}/rooms/${roomId}/video-url`)
  return res.data
}

export async function updateRoomPrompt(
  tourId: number,
  roomId: number,
  overrides: { motion?: string; reverse?: boolean },
): Promise<void> {
  await api.patch(`/tours/${tourId}/rooms/${roomId}/prompt`, overrides)
}

export async function generateComposite(tourId: number, clipOrder: number[]): Promise<void> {
  await api.post(`/tours/${tourId}/composite`, { clip_order: clipOrder })
}

export async function getCompositeDownloadUrl(tourId: number): Promise<{ url: string }> {
  const res = await api.get<{ url: string }>(`/tours/${tourId}/composite-download`)
  return res.data
}

// ── Listing import (Rightmove) ───────────────────────────────────────────────

export interface ListingImportResponse {
  job_id: number
  status: string
}

export type ListingImportStatus =
  | 'pending'
  | 'fetching'
  | 'awaiting_selection'
  | 'completed'
  | 'failed'

export interface ListingImportStatusResponse {
  job_id: number
  status: ListingImportStatus
  candidate_photos: string[] | null
  error: string | null
  created_at: string
  updated_at: string
}

export async function importListing(
  tourId: number,
  url: string,
): Promise<ListingImportResponse> {
  const res = await api.post<ListingImportResponse>(
    `/tours/${tourId}/import-listing`,
    { url },
  )
  return res.data
}

export async function getListingImportStatus(
  tourId: number,
  jobId: number,
): Promise<ListingImportStatusResponse> {
  const res = await api.get<ListingImportStatusResponse>(
    `/tours/${tourId}/import-listing/${jobId}`,
  )
  return res.data
}

// ── Batch URL import (listing photos downloaded server-side) ─────────────────

export async function batchImportUrls(
  tourId: number,
  rooms: { url: string; filename: string; room_order?: number; is_staged?: boolean }[],
): Promise<{ rooms: { room_id: number; s3_key: string }[] }> {
  const res = await api.post<{ rooms: { room_id: number; s3_key: string }[] }>(
    `/tours/${tourId}/rooms/batch-import-urls`,
    { rooms },
  )
  return res.data
}


